import React, { useState, useMemo } from 'react';
import { Search, User, Users, Shield, ChevronDown, Briefcase, TrendingUp } from 'lucide-react';
import { useAuthDemo } from '@/domains/billing/hooks/shared/useAuthUnified';
import type { SystemUserWithEmployeeData } from '@/shared/hooks/useSystemUsers';

interface DemoEmployeeSelectorProps {
  className?: string;
}

// Quick filter options based on group membership
const QUICK_FILTERS = [
  { label: 'All', filter: () => true, icon: Users },
  { label: 'Admin', filter: (emp: SystemUserWithEmployeeData) => emp.groups?.includes('admin'), icon: Shield },
  { label: 'Senior Managers', filter: (emp: SystemUserWithEmployeeData) => emp.groups?.includes('senior-managers'), icon: TrendingUp },
  { label: 'Manager', filter: (emp: SystemUserWithEmployeeData) => emp.groups?.includes('manager'), icon: Briefcase },
  { label: 'Member Only', filter: (emp: SystemUserWithEmployeeData) => emp.groups?.length === 1 && emp.groups[0] === 'member', icon: User },
];

/**
 * Employee Selector Component for Demo Mode
 * Provides searchable dropdown of all employees with filtering capabilities
 */
export const DemoEmployeeSelector: React.FC<DemoEmployeeSelectorProps> = ({ className = '' }) => {
  const {
    availableEmployees,
    demoEmployee: selectedEmployee,
    switchToDemo,
    isDevEnvironment
  } = useAuthDemo();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState(0); // Index of QUICK_FILTERS
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Don't render in production
  if (!isDevEnvironment) {
    return null;
  }

  // Filter and search employees
  const filteredEmployees = useMemo(() => {
    let filtered = availableEmployees.filter(QUICK_FILTERS[selectedFilter].filter);

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(emp => 
        emp.employee_data?.name?.toLowerCase().includes(query) ||
        emp.email.toLowerCase().includes(query) ||
        emp.employee_data?.team_name?.toLowerCase().includes(query) ||
        emp.employee_data?.role_name?.toLowerCase().includes(query)
      );
    }

    // Sort by name
    return filtered.sort((a, b) => {
      const nameA = a.employee_data?.name || '';
      const nameB = b.employee_data?.name || '';
      return nameA.localeCompare(nameB);
    });
  }, [availableEmployees, searchQuery, selectedFilter]);

  const handleEmployeeSelect = (employee: SystemUserWithEmployeeData) => {
    switchToDemo(employee);
    setIsDropdownOpen(false);
    setSearchQuery('');
  };

  const getGroupBadges = (employee: SystemUserWithEmployeeData): string[] => {
    return (employee.groups || []).map(g => g.charAt(0).toUpperCase() + g.slice(1).replace(/-/g, ' '));
  };

  const getEmployeeInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Quick Filters */}
      <div className="mb-3">
        <div className="flex flex-wrap gap-1">
          {QUICK_FILTERS.map((filter, index) => {
            const Icon = filter.icon;
            const isSelected = selectedFilter === index;
            return (
              <button
                key={index}
                onClick={() => setSelectedFilter(index)}
                className={`text-xs px-2 py-1 rounded-md flex items-center gap-1 transition-colors ${
                  isSelected
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <Icon className="h-3 w-3" />
                {filter.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Search Input */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search employees..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsDropdownOpen(true)}
          className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Selected Employee Display */}
      {selectedEmployee && !isDropdownOpen && (
        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            {/* Avatar */}
            <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
              {getEmployeeInitials(selectedEmployee.employee_data?.name || 'UN')}
            </div>
            
            {/* Employee Info */}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900">
                {selectedEmployee.employee_data?.name || 'Unknown Employee'}
              </div>
              <div className="text-sm text-gray-600">
                {selectedEmployee.employee_data?.role_name} • {selectedEmployee.employee_data?.team_name}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {selectedEmployee.email}
              </div>
              
              {/* Permission Badges */}
              <div className="flex flex-wrap gap-1 mt-2">
                {getGroupBadges(selectedEmployee).map(badge => (
                  <span
                    key={badge}
                    className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full"
                  >
                    {badge}
                  </span>
                ))}
              </div>
            </div>

            {/* Dropdown Toggle */}
            <button
              onClick={() => setIsDropdownOpen(true)}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Employee Dropdown */}
      {isDropdownOpen && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-y-auto">
          {filteredEmployees.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              No employees found matching your criteria
            </div>
          ) : (
            <div className="py-1">
              {filteredEmployees.map((employee) => (
                <button
                  key={employee.id}
                  onClick={() => handleEmployeeSelect(employee)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="w-8 h-8 bg-gray-500 text-white rounded-full flex items-center justify-center text-xs font-medium">
                      {getEmployeeInitials(employee.employee_data?.name || 'UN')}
                    </div>
                    
                    {/* Employee Info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm">
                        {employee.employee_data?.name || 'Unknown Employee'}
                      </div>
                      <div className="text-xs text-gray-600">
                        {employee.employee_data?.role_name} • {employee.employee_data?.team_name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {employee.email}
                      </div>
                      
                      {/* Permission Badges */}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {getGroupBadges(employee).map(badge => (
                          <span
                            key={badge}
                            className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded"
                          >
                            {badge}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Click outside to close dropdown */}
      {isDropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsDropdownOpen(false)}
        />
      )}
    </div>
  );
};

